import React, { useRef } from 'react';
import { Box, Flex, Heading, HStack, IconButton, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { SealButton } from './SealButton';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
import { services, type Service } from '../data/content';
import { whatsappHref } from '../data/brand';
function ServiceCard({
  service
}: {
  service: Service;
}) {
  return <Box as="article" role="group" position="relative" flex="0 0 auto" alignSelf="stretch" w={{
    base: '17.5rem',
    sm: '20rem',
    md: '23rem'
  }} display="flex" flexDirection="column" scrollSnapAlign="center" borderRadius="0" overflow="hidden" bg="ink.900" border="1px solid" borderColor="cream.200" sx={{
    clipPath: 'polygon(0 0, 100% 0, 100% 92%, 88% 100%, 0 100%)'
  }} transition="transform 0.35s ease, box-shadow 0.35s ease" _hover={{
    transform: 'translateY(-6px)',
    boxShadow: '0 26px 60px rgba(13,13,13,0.35)'
  }} data-id="element-151" data-mp="z4wearhljpz6">
      <Box position="relative" w="100%" bg="ink.800" sx={{
      aspectRatio: '3 / 2'
    }} overflow="hidden" flexShrink={0} data-id="element-152" data-mp="3b3zlkxqc5ml6">
        <Box as="img" src={service.image} alt={service.imageAlt} loading="lazy" w="100%" h="100%" objectFit="contain" transition="transform 0.6s ease" _groupHover={{
        transform: 'scale(1.03)'
      }} data-id="element-153" data-mp="32acsxghfczg7" />
      </Box>

      <Box px={{
      base: 5,
      md: 6
    }} pt={5} pb={6} bg="ink.900" borderTop="2px solid" borderColor="crimson.500" flex="1" display="flex" flexDirection="column" data-id="element-154" data-mp="3g32fbm0v86du">
        <Heading as="h3" fontSize={{
        base: 'xl',
        md: '1.7rem'
      }} color="gold.300" mb={3} data-id="element-155" data-mp="24lsvuzhqlecj">
          {service.title}
        </Heading>
        <Text fontSize="sm" color="cream.200" opacity={0.85} mb={5} flex="1" data-id="element-156" data-mp="1pk2tozc601p4">
          {service.description}
        </Text>
        <Box alignSelf="center" data-id="element-157" data-mp="1li8wx253bhu8">
          <SealButton href={whatsappHref(service.title)} external tone="whatsapp" label="Consultar ahora" ariaLabel={`Consultar por WhatsApp sobre ${service.title}`} icon={<WhatsAppIcon size={15} data-id="element-159" data-mp="16tl5n82a1ua3" />} size="md" data-id="element-158" data-mp="1lj0rqaqv5bjg" />
        </Box>
      </Box>
    </Box>;
}
export function Services() {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const amount = Math.min(track.clientWidth * 0.8, 380);
    track.scrollBy({
      left: amount * direction,
      behavior: 'smooth'
    });
  };
  return <Box as="section" id="servicios" bg="cream.50" color="ink.900" position="relative" pt={{
    base: 16,
    md: 28
  }} pb={{
    base: 16,
    md: 24
  }} mt={{
    md: '-4vw'
  }} sx={{
    clipPath: {
      base: 'none',
      md: 'polygon(0 4vw, 100% 0, 100% 100%, 0 100%)'
    }
  }} data-id="element-160" data-mp="2s5wcom019gbv">
      <Box px={{
      base: 5,
      md: 10,
      xl: 16
    }} data-id="element-161" data-mp="2zovuin7uizt4">
        <Reveal data-id="element-162" data-mp="34hf8vmecxyyz">
          <SectionHeading eyebrow="Acompañamiento espiritual" title="Nuestros Servicios" description="Cada consulta se realiza de manera personalizada. Conversamos contigo sobre tu situación y te explicamos en qué consiste cada práctica antes de comenzar." data-id="element-163" data-mp="34gnfmi88gdbv" />
        </Reveal>

        <Flex justify="flex-end" mt={{
        base: 8,
        md: 10
      }} mb={4} data-id="element-164" data-mp="2gsduliiuftls">
          <HStack spacing={2} data-id="element-165" data-mp="2lq5ylawrj66c">
            <IconButton aria-label="Ver servicios anteriores" icon={<ChevronLeftIcon size={20} data-id="element-167" data-mp="2psj1lrk4kpzf" />} onClick={() => scrollByCard(-1)} variant="crimsonOutline" borderRadius="full" size="lg" data-id="element-166" data-mp="2lpw7fjzzesz2" />
            <IconButton aria-label="Ver más servicios" icon={<ChevronRightIcon size={20} data-id="element-169" data-mp="3kmlr01450rk" />} onClick={() => scrollByCard(1)} variant="crimsonOutline" borderRadius="full" size="lg" data-id="element-168" data-mp="51a63mtusr4f" />
          </HStack>
        </Flex>
      </Box>

      <Box ref={trackRef} className="hide-scrollbar" display="flex" gap={{
      base: 4,
      md: 6
    }} overflowX="auto" px={{
      base: 5,
      md: 10,
      xl: 16
    }} pb={4} sx={{
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
      overscrollBehaviorX: 'contain'
    }} data-id="element-170" data-mp="dknc35xegh59">
        {services.map(service => <ServiceCard key={service.title} service={service} data-id="element-171" data-mp="w90jrfs7bx34" />)}
      </Box>

      <Text px={{
      base: 5,
      md: 10,
      xl: 16
    }} mt={4} fontSize="xs" color="ink.700" opacity={0.6} display={{
      base: 'block',
      md: 'none'
    }} data-id="element-172" data-mp="rbs17e355r1c">
        Desliza para ver todos los servicios →
      </Text>
    </Box>;
}